import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getEmployee } from "@/services/getEmployeeDetails";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button, buttonVariants } from "@/components/ui/button";
import { ArrowLeft, CalendarIcon, ChevronLeft } from "lucide-react";
import { updateEmployee } from "@/services/updateEmployee";
import { toast } from "sonner";
import { countries } from "@/data/countries";
import { Role } from "@/core/auth/models/role.model";
import RoleGuard from "@/core/auth/components/role-guard";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { EmployeeFormData, employeeSchema } from "../schemas/employee.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  Form,
} from "@/components/ui/form";
import { cn, setDateToNoon } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";

// Helper function to format date
const formatDateOfBirth = (dateString: any): string => {
  if (!dateString) return "-";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

function EmployeeDetails() {
  const { id } = useParams();
  const [isEditable, setIsEditable] = useState(false);
  const queryClient = useQueryClient();

  const editEmployeeForm = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      phoneNumber: "",
    }, // Initial default values
  });

  const {
    data: employeeData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["employee", id],
    queryFn: async () => getEmployee(id),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: (updatedEmployee: any) =>
      updateEmployee(updatedEmployee._id, updatedEmployee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee", id] });
      toast.success("Employee updated successfully");
      setIsEditable(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Error updating employee details.");
    },
  });

  const handleEditToggle = () => {
    setIsEditable((prev) => !prev);
    if (!isEditable && employeeData) {
      // Populate form with employee data
      editEmployeeForm.reset({
        name: employeeData.name || "",
        email: employeeData.email || "",
        role: employeeData.role || "",
        company: employeeData.company?.name || "",
        phoneNumber: employeeData.phoneNumber || "",
        nationality: employeeData.nationality || "",
        idNumber: employeeData.idNumber || "",
        address: employeeData.address || "",
        // dateOfBirth: formatDateOfBirth(employeeData.dateOfBirth),
        dateOfBirth: employeeData.dateOfBirth
          ? new Date(employeeData.dateOfBirth)
          : undefined,
      });
    }
  };

  const onSubmit = (data: EmployeeFormData) => {
    const payload = {
      ...employeeData,
      ...data,
      company: employeeData?.company?._id || employeeData?.company,
    };
    mutation.mutate(payload);
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading employee details.</div>;

  return (
    <div className="min-h-screen p-4">
      <div className="W-FULL mx-auto">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/app/employees"
              className={`${buttonVariants({
                variant: "ghost",
              })}  flex items-center gap-2`}
            >
              {/* <ChevronLeft className="h-4 w-4" />
               */}
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="items-center gap-4">
              <h1 className="text-xl font-medium">Employee Details</h1>
              <RoleGuard requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}>
                <p className="text-gray-500">View and edit employee details</p>
              </RoleGuard>
              <RoleGuard requiredRoles={[Role.Employee]}>
                <p className="text-gray-500">View employee details</p>
              </RoleGuard>
            </div>
          </div>

          <RoleGuard requiredRoles={[Role.CompanyAdmin, Role.SuperAdmin]}>
            <Button
              onClick={handleEditToggle}
              variant={isEditable ? "secondary" : "default"}
              className="rounded-md"
            >
              {isEditable ? "Cancel Editing" : "Edit Details"}
            </Button>
          </RoleGuard>
        </div>

        <div className=" rounded-lg shadow-sm border  overflow-hidden">
          <div className="p-6 border-b  ">
            <h1 className="text-2xl font-semibold ">
              {employeeData?.name || "Employee Details"}
            </h1>
            <p className="text-gray-500 mt-1">{employeeData?.email || ""}</p>
          </div>

          <div className="p-6">
            {!isEditable ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  { label: "Role", value: employeeData?.role },
                  { label: "Company", value: employeeData?.company?.name },
                  { label: "Phone Number", value: employeeData?.phoneNumber },
                  { label: "Nationality", value: employeeData?.nationality },
                  { label: "ID Number", value: employeeData?.idNumber },
                  { label: "Address", value: employeeData?.address },
                  {
                    label: "Date of Birth",
                    value: formatDateOfBirth(employeeData?.dateOfBirth),
                  },
                ].map((field, index) => (
                  <div key={index} className="space-y-2 ">
                    <Label className="text-sm text-gray-500">
                      {field.label}
                    </Label>
                    <p className="text-base font-medium  p- capitalize  rounded-lg ">
                      {field.value || "-"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <Form {...editEmployeeForm}>
                <form
                  onSubmit={editEmployeeForm.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Edit Mode Fields */}
                    <div>
                      <Label htmlFor="name" className="text-sm text-gray-500">
                        Name
                      </Label>
                      <Controller
                        name="name"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                      />
                      {editEmployeeForm.formState?.errors?.name && (
                        <span className="text-sm text-red-500 mt-1">
                          {editEmployeeForm.formState?.errors?.name.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm text-gray-500">
                        Email
                      </Label>
                      <Controller
                        name="email"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                      />
                      {editEmployeeForm.formState.errors.email && (
                        <span className="text-sm text-red-500 mt-1">
                          {editEmployeeForm.formState.errors.email.message}
                        </span>
                      )}
                    </div>
                    {/* <div>
                      <Label htmlFor="role" className="text-sm text-gray-500">
                        Role
                      </Label>
                      <Controller
                        name="role"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                      />
                      {editEmployeeForm.formState.errors.role && (
                        <span className="text-sm text-red-500 mt-1">
                          {editEmployeeForm.formState.errors.role.message}
                        </span>
                      )}
                    </div> */}
                    <div>
                      <Label
                        htmlFor="phoneNumber"
                        className="text-sm text-gray-500"
                      >
                        Phone Number
                      </Label>
                      <Controller
                        name="phoneNumber"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                      />
                      {editEmployeeForm.formState.errors.phoneNumber && (
                        <span className="text-sm text-red-500 mt-1">
                          {
                            editEmployeeForm.formState.errors.phoneNumber
                              .message
                          }
                        </span>
                      )}
                    </div>
                    <div>
                      <Label
                        htmlFor="nationality"
                        className="text-sm text-gray-500"
                      >
                        Nationality
                      </Label>
                      <Controller
                        name="nationality"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Nationality" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>Countries</SelectLabel>
                                {countries.map((country) => (
                                  <SelectItem
                                    key={country.id}
                                    value={country.name}
                                  >
                                    {country.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {editEmployeeForm.formState.errors.nationality && (
                        <span className="text-sm text-red-500 mt-1">
                          {
                            editEmployeeForm.formState.errors.nationality
                              .message
                          }
                        </span>
                      )}
                    </div>
                    <div>
                      <Label
                        htmlFor="idNumber"
                        className="text-sm text-gray-500"
                      >
                        ID Number
                      </Label>
                      <Controller
                        name="idNumber"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                      />
                      {editEmployeeForm.formState.errors.idNumber && (
                        <span className="text-sm text-red-500 mt-1">
                          {editEmployeeForm.formState.errors.idNumber.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Label
                        htmlFor="address"
                        className="text-sm text-gray-500"
                      >
                        Address
                      </Label>
                      <Controller
                        name="address"
                        control={editEmployeeForm.control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-primary/20"
                          />
                        )}
                      />
                      {editEmployeeForm.formState.errors.address && (
                        <span className="text-sm text-red-500 mt-1">
                          {editEmployeeForm.formState.errors.address.message}
                        </span>
                      )}
                    </div>
                    <div>
                      {/* <Label
                      htmlFor="dateOfBirth"
                      className="text-sm text-gray-500"
                    >
                      Date of Birth
                    </Label>
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select date of birth"
                          className="mt-1"
                        />
                      )}
                    />
                    {errors.dateOfBirth && (
                      <span className="text-sm text-red-500 mt-1">
                        {errors.dateOfBirth.message}
                      </span>
                    )} */}
                      <FormField
                        control={editEmployeeForm.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Date of birth</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0"
                                align="start"
                              >
                                <Calendar
                                  mode="single"
                                  selected={setDateToNoon(field.value)}
                                  onSelect={(date) =>
                                    field.onChange(setDateToNoon(date))
                                  }
                                  disabled={(date) => {
                                    const todayNoon = setDateToNoon(new Date());
                                    const minDateNoon = setDateToNoon(
                                      new Date("1900-01-01")
                                    );
                                    return (
                                      date > todayNoon || date < minDateNoon
                                    );
                                  }}
                                  captionLayout="dropdown"
                                />
                              </PopoverContent>
                            </Popover>
                            {/* <FormDescription>
                              Your date of birth is used to calculate your age.
                            </FormDescription> */}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-4 pt-6 border-t">
                    <Button type="submit" className="px-8">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetails;
